import React, { FC } from 'react';

import styled from '@emotion/styled';
import ReactSelect, { ActionMeta, Props } from 'react-select';
import { variant } from '@lucid/theme';
import { FieldRenderProps } from 'react-final-form';

import FieldWrapper from '../field-wrapper/field-wrapper';
import { Component, Marketo } from '../../../types/types';
import { isFieldObject } from '../../marketo-form/validation/validation';

export interface DropdownProps
  extends FieldRenderProps<Marketo.FormFieldStateValues, any>,
    Props<Marketo.FormFieldStateValues> {
  /** Allows passing additional styles to the component. */
  className?: string;
  /** The id that links the form input with the label. */
  id: string;
  /** The available variants for the Dropdown component. */
  variant?: Component.Type['Form']['Dropdown'];
}

const StyledReactSelect = styled(ReactSelect)(
  variant({
    scale: 'form.dropdown',
    variants: {
      defaultVariant: {},
    },
  }),
  ({ hasError, required, theme }) => [
    {
      label: 'reactSelectDropdown',
    },
    required &&
      hasError && {
        '& .reactSelectDropdown__control': {
          border: `1px solid ${theme.colors.pink[60]}`,
        },
      },
  ],
);

const Dropdown: FC<DropdownProps> = ({
  className,
  customErrorMessage,
  id,
  input,
  isDisabled = false,
  isFullWidth = false,
  isHidden = false,
  isRequired = false,
  label,
  meta,
  message,
  variant = 'default',
  ...rest
}) => {
  const hasError = meta?.error && meta?.touched;
  const errorMessage = customErrorMessage || meta?.error;

  const handleOnChange = (
    option: Marketo.FormFieldStateValues,
    actionMeta: ActionMeta<Marketo.FormFieldStateValues>,
  ) => {
    if (isFieldObject(option)) {
      if (actionMeta.action === 'select-option') {
        const currentOption = {
          ...option,
          value: option.value || '',
        };

        input.onChange(currentOption);
      }
    }
  };

  return (
    <FieldWrapper
      isDropdown
      className={className}
      customErrorMessage={errorMessage}
      hasError={hasError}
      id={id}
      isDisabled={isDisabled}
      isFullWidth={isFullWidth}
      isHidden={isHidden}
      isRequired={isRequired}
      label={label}
    >
      <StyledReactSelect
        {...input}
        {...rest}
        aria-label={id}
        aria-labelledby={id}
        className={className}
        classNamePrefix="reactSelectDropdown"
        hasError={hasError}
        inputId={id}
        onChange={handleOnChange}
        required={isRequired}
        variant={variant}
      />
    </FieldWrapper>
  );
};

export { Dropdown };
export default Dropdown;
